import React, { useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../../api';

const today = () => new Date().toISOString().slice(0, 10);

const defaultMaturityDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 3);
  return date.toISOString().slice(0, 10);
};

const formatAmount = value => (Number(value) || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fieldStyle = {
  width: '100%',
  padding: 8,
  borderRadius: 4,
  border: '1px solid #cfd8e3',
  boxSizing: 'border-box',
};

function GoldRepledgeModal({ loan, loans = [], onClose, onSuccess }) {
  const selectedLoans = loans.length ? loans : loan ? [loan] : [];
  const primaryLoan = selectedLoans[0];
  const activePledge = selectedLoans.length === 1 && ['Pledged', 'Overdue'].includes(primaryLoan?.repledge_status);
  const selectedLoanTotal = selectedLoans.reduce((total, selectedLoan) => total + (Number(selectedLoan.issue_amount) || 0), 0);
  const [banks, setBanks] = useState([]);
  const [selectedBankName, setSelectedBankName] = useState('');
  const [form, setForm] = useState({
    bank_id: '',
    pledge_number: '',
    pledge_date: today(),
    principal_amount: '',
    annual_interest_rate: '',
    maturity_date: defaultMaturityDate(),
    bank_charges: '',
    notes: '',
  });
  const [releaseDate, setReleaseDate] = useState(today());
  const [releaseNotes, setReleaseNotes] = useState('');
  const [ornamentPhotos, setOrnamentPhotos] = useState([]);
  const [ornamentPhotoPreviews, setOrnamentPhotoPreviews] = useState([]);
  const [savedOrnamentPhotos, setSavedOrnamentPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/gold-repledge-banks`)
      .then(response => (response.ok ? response.json() : []))
      .then(data => setBanks(Array.isArray(data) ? data : []))
      .catch(() => setBanks([]));
  }, []);

  useEffect(() => {
    const previews = ornamentPhotos.map(file => ({ file, url: URL.createObjectURL(file) }));
    setOrnamentPhotoPreviews(previews);
    return () => previews.forEach(preview => URL.revokeObjectURL(preview.url));
  }, [ornamentPhotos]);

  useEffect(() => {
    if (!activePledge || !primaryLoan?.pledge_id) return undefined;

    let cancelled = false;
    const objectUrls = [];
    setPhotosLoading(true);
    fetch(`${API_BASE_URL}/api/gold-repledges/${primaryLoan.pledge_id}/photos`)
      .then(async response => {
        const data = await response.json().catch(() => ([]));
        if (!response.ok) throw new Error(data.error || 'Unable to load ornament photos');
        return Promise.all(data.map(async photo => {
          const imageResponse = await fetch(`${API_BASE_URL}/api/gold-repledges/${primaryLoan.pledge_id}/photos/${photo.photo_id}/view`);
          if (!imageResponse.ok) return null;
          const url = URL.createObjectURL(await imageResponse.blob());
          if (cancelled) {
            URL.revokeObjectURL(url);
            return null;
          }
          objectUrls.push(url);
          return { ...photo, url };
        }));
      })
      .then(photos => {
        if (!cancelled) setSavedOrnamentPhotos(photos.filter(Boolean));
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Unable to load ornament photos');
      })
      .finally(() => {
        if (!cancelled) setPhotosLoading(false);
      });

    return () => {
      cancelled = true;
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [activePledge, primaryLoan?.pledge_id]);

  const bankNames = useMemo(() => (
    [...new Set(banks.map(bank => bank.bank_name))]
  ), [banks]);
  const branchOptions = useMemo(() => (
    banks.filter(bank => bank.bank_name === selectedBankName)
  ), [banks, selectedBankName]);

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
    if (error) setError('');
  };

  const selectOrnamentPhotos = event => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
    if (selected.some(file => !allowedTypes.has(file.type))) {
      setError('Only JPEG, PNG and WebP ornament photos are allowed');
      return;
    }
    if (selected.some(file => file.size > 10 * 1024 * 1024)) {
      setError('Each ornament photo must be 10 MB or smaller');
      return;
    }
    if (ornamentPhotos.length + selected.length > 10) {
      setError('You can upload a maximum of 10 ornament photos');
      return;
    }
    setOrnamentPhotos(current => [...current, ...selected]);
    if (error) setError('');
  };

  const createPledge = async event => {
    event.preventDefault();
    setError('');

    const loanIds = selectedLoans
      .map(selectedLoan => String(selectedLoan?.loan_id || '').trim())
      .filter(Boolean);
    if (!Number.isInteger(Number.parseInt(form.bank_id, 10))) {
      setError('Please select a bank branch');
      return;
    }
    if (!String(form.pledge_number || '').trim()) {
      setError('Please enter the bank pledge number');
      return;
    }
    if (!loanIds.length) {
      setError('Please select at least one Gold Loan');
      return;
    }
    if (!form.pledge_date) {
      setError('Please select the pledge date');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      // Repeated multipart fields are parsed reliably by Multer as an array and
      // avoid depending on JSON parsing inside a multipart/form-data request.
      loanIds.forEach(loanId => formData.append('loan_ids', loanId));
      ornamentPhotos.forEach(photo => formData.append('ornament_photos', photo));
      const response = await fetch(`${API_BASE_URL}/api/gold-repledges`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to create bank pledge');
      onSuccess();
    } catch (err) {
      setError(err.message || 'Unable to create bank pledge');
    } finally {
      setSubmitting(false);
    }
  };

  const releasePledge = async event => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/gold-repledges/loans/${primaryLoan.loan_id}/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ release_date: releaseDate, release_notes: releaseNotes }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to release gold from bank');
      onSuccess();
    } catch (err) {
      setError(err.message || 'Unable to release gold from bank');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <form className="mobile-modal-panel" onSubmit={activePledge ? releasePledge : createPledge} style={{ background: '#fff', borderRadius: 8, boxShadow: '0 3px 20px rgba(0,0,0,0.2)', padding: 24, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 4px', color: 'navy' }}>{activePledge ? 'Bank Pledge Details' : 'Re-Pledge Gold with Bank'}</h3>
        <div style={{ color: '#667085', marginBottom: 18 }}>
          {activePledge
            ? `Loan ${primaryLoan.loan_id} · ${primaryLoan.customer_name} · ${formatAmount(primaryLoan.issue_amount)}`
            : `${selectedLoans.length} Gold Loan${selectedLoans.length === 1 ? '' : 's'} selected · Customer loan total ${formatAmount(selectedLoanTotal)}`}
        </div>

        {activePledge ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 18 }}>
              <div><strong>Bank</strong><br />{primaryLoan.repledge_bank_name || '-'}</div>
              <div><strong>Branch</strong><br />{primaryLoan.repledge_branch_name || '-'}</div>
              <div><strong>Pledge Number</strong><br />{primaryLoan.pledge_number || '-'}</div>
              <div><strong>Total Bank Pledge Amount</strong><br />{formatAmount(primaryLoan.repledge_amount)}</div>
              <div><strong>Pledge Date</strong><br />{primaryLoan.repledge_date ? String(primaryLoan.repledge_date).slice(0, 10) : '-'}</div>
              <div><strong>Maturity Date</strong><br />{primaryLoan.repledge_maturity_date ? String(primaryLoan.repledge_maturity_date).slice(0, 10) : '-'}</div>
              <div><strong>Bank Interest</strong><br />{Number(primaryLoan.repledge_interest_rate || 0).toFixed(2)}%</div>
              <div><strong>Status</strong><br />{primaryLoan.repledge_status}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <h4 style={{ margin: '0 0 10px' }}>Pledged Ornament Photos</h4>
              {photosLoading ? (
                <div style={{ color: '#667085' }}>Loading photos...</div>
              ) : savedOrnamentPhotos.length ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                  {savedOrnamentPhotos.map(photo => (
                    <a key={photo.photo_id} href={photo.url} target="_blank" rel="noreferrer" title={photo.file_name} style={{ display: 'block' }}>
                      <img src={photo.url} alt={photo.file_name || 'Pledged ornament'} style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6, border: '1px solid #d0d5dd' }} />
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#667085' }}>No ornament photos were uploaded for this pledge.</div>
              )}
            </div>
            <h4 style={{ margin: '0 0 10px' }}>Release Gold from Bank</h4>
            <label style={{ display: 'grid', gap: 5, marginBottom: 10 }}>Release Date
              <input type="date" value={releaseDate} onChange={event => setReleaseDate(event.target.value)} required style={fieldStyle} />
            </label>
            <label style={{ display: 'grid', gap: 5 }}>Release Notes
              <textarea value={releaseNotes} onChange={event => setReleaseNotes(event.target.value)} rows={3} style={{ ...fieldStyle, resize: 'vertical' }} />
            </label>
          </>
        ) : (
          <>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 14, maxHeight: 170, overflowY: 'auto' }}>
              {selectedLoans.map(selectedLoan => (
                <div key={selectedLoan.loan_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 10px', borderBottom: '1px solid #eef2f6' }}>
                  <span><strong>{selectedLoan.loan_id}</strong> · {selectedLoan.customer_name}</span>
                  <span style={{ whiteSpace: 'nowrap' }}>{formatAmount(selectedLoan.issue_amount)}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              <label style={{ display: 'grid', gap: 5 }}>Bank Name
                <select
                  value={selectedBankName}
                  onChange={event => {
                    setSelectedBankName(event.target.value);
                    setForm(current => ({ ...current, bank_id: '' }));
                    if (error) setError('');
                  }}
                  required
                  style={fieldStyle}
                >
                  <option value="">Select bank</option>
                  {bankNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 5 }}>Branch
                <select name="bank_id" value={form.bank_id} onChange={handleChange} required disabled={!selectedBankName} style={fieldStyle}>
                  <option value="">Select branch</option>
                  {branchOptions.map(bank => <option key={bank.bank_id} value={bank.bank_id}>{bank.branch_name}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 5 }}>Bank Pledge Number
                <input name="pledge_number" value={form.pledge_number} onChange={handleChange} required style={fieldStyle} />
              </label>
              <label style={{ display: 'grid', gap: 5 }}>Pledge Date
                <input name="pledge_date" type="date" value={form.pledge_date} onChange={handleChange} required style={fieldStyle} />
              </label>
              <label style={{ display: 'grid', gap: 5 }}>Total Amount Received from Bank
                <input name="principal_amount" type="number" min="0.01" step="0.01" value={form.principal_amount} onChange={handleChange} required style={fieldStyle} />
              </label>
              <label style={{ display: 'grid', gap: 5 }}>Annual Interest Rate %
                <input name="annual_interest_rate" type="number" min="0" step="0.01" value={form.annual_interest_rate} onChange={handleChange} required style={fieldStyle} />
              </label>
              <label style={{ display: 'grid', gap: 5 }}>Maturity Date
                <input name="maturity_date" type="date" value={form.maturity_date} onChange={handleChange} style={fieldStyle} />
              </label>
              <label style={{ display: 'grid', gap: 5 }}>Bank Charges
                <input name="bank_charges" type="number" min="0" step="0.01" value={form.bank_charges} onChange={handleChange} style={fieldStyle} />
              </label>
            </div>
            {!banks.length && <div style={{ color: '#b54708', marginTop: 10 }}>No active bank branches found. Ask an Admin to create one under Settings → Gold Pledge Banks.</div>}
            <label style={{ display: 'grid', gap: 5, marginTop: 12 }}>Notes
              <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} style={{ ...fieldStyle, resize: 'vertical' }} />
            </label>
            <div style={{ marginTop: 14 }}>
              <label style={{ display: 'grid', gap: 5 }}>Photos of Pledged Ornaments
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={selectOrnamentPhotos}
                  style={{ ...fieldStyle, padding: 6 }}
                />
              </label>
              <div style={{ color: '#667085', fontSize: 12, marginTop: 5 }}>Optional · Up to 10 JPEG, PNG or WebP photos · 10 MB maximum each</div>
              {ornamentPhotoPreviews.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10, marginTop: 10 }}>
                  {ornamentPhotoPreviews.map((preview, index) => (
                    <div key={`${preview.file.name}-${preview.file.lastModified}-${index}`} style={{ position: 'relative' }}>
                      <img src={preview.url} alt={preview.file.name} style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6, border: '1px solid #d0d5dd' }} />
                      <button
                        type="button"
                        aria-label={`Remove ${preview.file.name}`}
                        onClick={() => setOrnamentPhotos(current => current.filter((_, photoIndex) => photoIndex !== index))}
                        style={{ position: 'absolute', top: 4, right: 4, width: 25, height: 25, border: 'none', borderRadius: 999, background: 'rgba(180, 35, 24, 0.9)', color: '#fff', cursor: 'pointer', fontWeight: 700 }}
                      >
                        ×
                      </button>
                      <div title={preview.file.name} style={{ fontSize: 11, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview.file.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {error && <div style={{ color: '#b42318', marginTop: 12 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button type="button" onClick={onClose} disabled={submitting} style={{ padding: '8px 18px', borderRadius: 4, border: '1px solid #ccc', background: '#f5f5f5' }}>Cancel</button>
          <button type="submit" disabled={submitting} style={{ padding: '8px 18px', borderRadius: 4, border: 'none', background: activePledge ? '#d97706' : '#7c3aed', color: '#fff', fontWeight: 600 }}>
            {submitting ? 'Saving...' : activePledge ? 'Confirm Bank Release' : 'Create Bank Pledge'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default GoldRepledgeModal;
